import { useState, useEffect } from "react";
import { getHeroContent, DEFAULT_HERO } from "@/lib/content";
import type { HeroContent } from "@/types/content";
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from "framer-motion";
import { getOptimizedImageUrl } from "@/lib/utils";

const HeroSection = () => {
  const [hero, setHero] = useState<HeroContent>(DEFAULT_HERO);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  
  const { scrollY } = useScroll();
  
  // Transform values based on scroll position
  const rawScale = useTransform(scrollY, [0, 200], [1, 0.95]);
  const rawBorderRadius = useTransform(scrollY, [0, 200], [0, 32]);
 
  // Apply spring physics for smoother transitions during scroll
  const springConfig = { stiffness: 120, damping: 25, restDelta: 0.001 };
  const scale = useSpring(rawScale, springConfig);
  const borderRadius = useSpring(rawBorderRadius, springConfig);

  useEffect(() => {
    getHeroContent(setHero).then((data) => {
      setHero(data);
    });
  }, []);

  useEffect(() => {
    if (!hero || !hero.heroImageUrls || hero.heroImageUrls.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % hero.heroImageUrls.length);
    }, 5000); // 5 seconds for a more relaxed feel

    return () => clearInterval(interval);
  }, [hero]);

  return (
    <div className="bg-[#FAF8F5] overflow-visible">
      <motion.section 
        style={isMobile ? {} : { 
          scale, 
          borderRadius,
          transformStyle: "preserve-3d",
          backfaceVisibility: "hidden",
          WebkitBackfaceVisibility: "hidden",
        }}
        className="relative w-full h-[75vh] md:h-[calc(100vh-96px)] flex items-center justify-center overflow-hidden bg-[#171717] will-change-transform"
      >
        {/* Background Image Slider with Framer Motion */}
        <div className="absolute inset-0 z-0 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.img
              key={currentImageIndex}
              src={getOptimizedImageUrl(
                hero.heroImageUrls?.[currentImageIndex] || "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?q=80&w=1600&auto=format&fit=crop",
                isMobile
              )}
              alt={`${import.meta.env.VITE_STORE_NAME || "QYISSA"} Premium Collection`}
              width="1920"
              height="1080"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5, ease: [0.4, 0, 0.2, 1] }}
              fetchpriority="high"
              className="w-full h-full object-contain md:object-contain"
            />
          </AnimatePresence>
        </div>

        {/* Small Explore Button */}
        <div className="absolute inset-0 z-10 flex items-end justify-center pb-8 md:pb-24">
          <motion.button 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 1 }}
            onClick={() => document.getElementById('products-list')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#171717]/95 text-white px-5 py-2.5 md:px-10 md:py-4 rounded-none font-body font-light text-[9px] md:text-xs uppercase tracking-[0.25em] hover:bg-[#FAF8F5] hover:text-[#171717] hover:border-[#171717] border border-transparent transition-all duration-300 shadow-none active:scale-98"
          >
            Explore Collection
          </motion.button>
        </div>

        {/* Slider Indicators */}
        {hero.heroImageUrls && hero.heroImageUrls.length > 1 && (
          <div className="absolute bottom-2 md:bottom-10 left-0 right-0 flex justify-center gap-4 z-10">
            {hero.heroImageUrls.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentImageIndex(i)}
                className="group p-2"
                aria-label={`Go to slide ${i + 1}`}
              >
                <div className={`h-1 transition-all duration-500 ${
                  i === currentImageIndex ? "bg-[#171717] w-12" : "bg-[#171717]/20 group-hover:bg-[#171717]/40 w-6"
                }`} />
              </button>
            ))}
          </div>
        )}
      </motion.section>
    </div>
  );
};

export default HeroSection;
